import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, setBearer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, ShieldAlert, ArrowLeft } from "lucide-react";

/**
 * ResetPassword — Phase 16.3
 *
 * Consumes a one-time token from the emailed link.  The token is
 * kept in component state and posted via body — never appended to
 * request URLs.  Backend validates + rotates + bumps auth_epoch
 * (invalidating every prior session).  On success we set the new
 * session and route to /app.
 */
export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [phase, setPhase] = React.useState("validating"); // validating|ok|invalid|done
  const [reason, setReason] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (!token) { setPhase("invalid"); setReason("no_token"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/reset/validate", { token });
        if (data.valid) {
          setEmail(data.email || "");
          setName(data.name || "");
          setPhase("ok");
        } else {
          setPhase("invalid");
          setReason(data.reason || "invalid");
        }
      } catch {
        setPhase("invalid"); setReason("network");
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (pw.length < 12) { setErr("Password must be at least 12 characters."); return; }
    if (pw !== pw2)     { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset", { token, new_password: pw });
      if (data?.token) setBearer(data.token);
      await refresh();
      setPhase("done");
      toast.success("Password updated — you're signed in.");
      setTimeout(() => nav("/app", { replace: true }), 1200);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(formatApiErrorDetail(detail) || "Reset failed.");
      // If token became invalid mid-flight (e.g. concurrent consume),
      // reroute to the invalid state so we don't keep offering the form.
      if (detail && detail.code === "token_invalid_or_expired") {
        setPhase("invalid"); setReason("consumed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-card border-r border-brand-rule">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-brand-violet flex items-center justify-center text-white font-bold text-[13px]">F</div>
          <div className="font-bold tracking-tight text-[15px] text-brand-ink">Fable Ops</div>
        </div>
        <div className="max-w-md">
          <div className="micro-label">Security</div>
          <h1 className="mt-2 text-[28px] font-bold tracking-tight text-brand-ink leading-tight">
            Choose a strong new password.
          </h1>
          <p className="mt-3 text-[13px] text-brand-muted">
            Minimum 12 characters. Can't be your name or email, and can't be
            on the common-password blocklist. Length beats complexity —
            passphrases are welcome.
          </p>
          <div className="mt-6 space-y-1.5">
            {[
              ["Min length",   "12 chars"],
              ["Blocklist",    "~1000 entries"],
              ["Session kill", "on save"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 text-[12px]">
                <span className="micro-label w-24">{k}</span>
                <span className="mono-num text-brand-ink">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-brand-faint">Phase 16.3 · one-time link, single use.</div>
      </div>

      <div className="flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/login"
                className="inline-flex items-center gap-1 text-[11px] text-brand-muted hover:text-brand-ink"
                data-testid="reset-back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>

          {phase === "validating" && (
            <div className="mt-6 text-[13px] text-brand-muted" data-testid="reset-validating">Verifying reset link…</div>
          )}

          {phase === "invalid" && (
            <div className="mt-6 rounded-md border border-rose-300 bg-rose-50 p-4" data-testid="reset-invalid">
              <div className="flex items-start gap-2">
                <ShieldAlert className="text-rose-700 mt-0.5" size={16} />
                <div>
                  <div className="text-[13px] font-semibold text-rose-900">
                    {reason === "expired"       ? "Link expired"
                     : reason === "already_used" ? "Link already used"
                     : reason === "superseded"   ? "A newer link supersedes this one"
                     : reason === "no_token"     ? "No reset token found"
                     : reason === "consumed"     ? "Link already used"
                     : "Reset link isn't valid"}
                  </div>
                  <div className="mt-1 text-[12px] text-rose-900/80 leading-relaxed">
                    Reset links expire 45 minutes after issue (15 minutes for admin-assisted).
                    Request a new one to try again.
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <button type="button" onClick={() => nav("/forgot-password")}
                        className="btn-outline !h-8" data-testid="reset-request-new">
                  Request a new link
                </button>
              </div>
            </div>
          )}

          {phase === "ok" && (
            <>
              <div className="mt-3 micro-label">Password reset</div>
              <h2 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">
                {name ? `Hi ${name.split(" ")[0]}` : "Set a new password"}
              </h2>
              <p className="mt-1 text-[12px] text-brand-muted">
                Signing back in as <span className="mono-num text-brand-ink">{email}</span>.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
                <div>
                  <label className="micro-label">New password</label>
                  <input type="password" required minLength={12} autoFocus
                         className="field-input mt-1" value={pw}
                         onChange={(e) => setPw(e.target.value)}
                         autoComplete="new-password"
                         data-testid="reset-new-password" />
                  <div className="mt-1 text-[10.5px] text-brand-muted">
                    Min. <span className="mono-num">12</span> characters. Not your name or email.
                  </div>
                </div>
                <div>
                  <label className="micro-label">Confirm password</label>
                  <input type="password" required minLength={12}
                         className="field-input mt-1" value={pw2}
                         onChange={(e) => setPw2(e.target.value)}
                         autoComplete="new-password"
                         data-testid="reset-confirm-password" />
                </div>
                {err && (
                  <div className="text-[12px] text-brand-coral" data-testid="reset-error">{err}</div>
                )}
                <button type="submit" className="btn-violet w-full" disabled={busy || !pw || !pw2}
                        data-testid="reset-submit">
                  {busy ? "Saving…" : "Update password"}
                </button>
              </form>
            </>
          )}

          {phase === "done" && (
            <div className="mt-6 rounded-md border border-emerald-300 bg-emerald-50 p-4"
                 data-testid="reset-done">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="text-emerald-700 mt-0.5" size={16} />
                <div>
                  <div className="text-[13px] font-semibold text-emerald-900">Password updated</div>
                  <div className="mt-1 text-[12px] text-emerald-900/80 leading-relaxed">
                    All other sessions have been signed out. Redirecting you now…
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
